import { RefreshCw } from "lucide-react"
import type { useRelistSettings } from "../../hooks/use-relist-settings"
import { formatRelativeTime } from "../../lib/format-date"
import { cn } from "../../lib/utils"
import { Button } from "../ui/button"

export function RelistSettingsOverview({
  settings,
  canEdit,
}: {
  settings: ReturnType<typeof useRelistSettings>
  canEdit: boolean
}) {
  const { inbox } = settings
  const rows = inbox.data?.marketplaces ?? []
  const blocked = rows.filter((row) => row.enabled && !row.executionEnabled).length
  return (
    <>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 id="listing-automation-title" className="text-lg font-semibold">
            Auto relist
          </h2>

          {inbox.data && (
            <p className="mt-1 text-sm text-muted-foreground">
              <span>{`${rows.length} marketplaces · ${rows.filter((row) => row.effectiveEnabled).length} on · `}</span>

              {blocked > 0 && <span className="text-warning">{`${blocked} blocked · `}</span>}

              <span>{`${rows.filter((row) => !row.enabled).length} off · ${rows.reduce((total, row) => total + row.pending + row.claimed, 0).toLocaleString()} changes waiting`}</span>
            </p>
          )}

          {!canEdit && (
            <p className="mt-2 text-sm text-muted-foreground">
              Only administrators can change auto relist.
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {inbox.dataUpdatedAt > 0 && (
            <span className="text-xs text-muted-foreground">
              Updated {formatRelativeTime(new Date(inbox.dataUpdatedAt).toISOString())}
            </span>
          )}

          <Button
            id="relist-refresh"
            onClick={() => void settings.refresh()}
            disabled={inbox.isFetching || settings.save.isPending}
          >
            <RefreshCw
              aria-hidden="true"
              className={cn("size-4", inbox.isFetching && "motion-safe:animate-spin")}
            />
            Refresh
          </Button>
        </div>
      </div>

      <details className="text-sm text-muted-foreground">
        <summary className="w-fit cursor-pointer rounded-sm py-2 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
          What auto relist does
        </summary>

        <ul className="mt-2 max-w-prose list-disc space-y-2 pl-5">
          <li>
            Creates and updates relist runs when tracked products change. Preparation, approval, and
            submission keep separate controls.
          </li>

          <li>
            First activation tracks future changes only. Re-enabling also includes changes detected
            while off.
          </li>

          <li>
            Waiting counts include known pending and claimed targets, excluding unread source
            events. Zero does not mean all changes have been checked. Permission does not confirm
            worker health.
          </li>

          <li>
            Undo restores only the preference. It cannot remove existing runs or reset the initial
            activation boundary.
          </li>
        </ul>
      </details>
    </>
  )
}
